import React from 'react';
import classNames from 'classnames';

import { getTestId } from 'helpers/test-helpers';

type BadgeVariant = 'primary' | 'secondary' | 'neutral' | 'reserve-outlined' | 'neutral-outlined';

interface VariantStyle {
  bgColor: string;
  textColor: string;
  borderClasses?: string;
}

const VARIANTS: Record<BadgeVariant, VariantStyle> = {
  primary: {
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
  },
  secondary: {
    bgColor: 'bg-success-100',
    textColor: 'text-success-600',
  },
  neutral: {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
  },
  'reserve-outlined': {
    bgColor: 'bg-white',
    textColor: 'text-navy-600',
    borderClasses: 'border border-navy-200',
  },
  'neutral-outlined': {
    bgColor: 'bg-white',
    textColor: 'text-gray-700',
    borderClasses: 'border border-gray-300',
  },
};

interface BadgeProps {
  text: string | null | undefined;
  icon?: React.ReactNode;
  className?: string;
  variant?: BadgeVariant;
}

const Badge = ({ text, icon, className, variant = 'primary' }: BadgeProps): React.JSX.Element | null => {
  if (!text) return null;

  const { bgColor, textColor, borderClasses } = VARIANTS[variant];
  const dataTestId = getTestId({ prefix: 'badge', label: text, suffix: 'label' });

  return (
    <div
      className={classNames(
        'flex items-center justify-center rounded px-2 py-1 gap-x-1',
        bgColor,
        textColor,
        borderClasses,
        className
      )}
      data-testid={dataTestId}
    >
      {icon}
      <span className="text-xs font-semibold">{text}</span>
    </div>
  );
};

export default Badge;
